import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { SignInButton, SignedIn, SignedOut, UserButton, useUser } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import {
  Archive,
  LayoutDashboard,
  LogIn,
  Users,
} from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { ContentWorkflowProvider } from "../../contentWorkflowContext";
import { isConfigured, serviceConfig } from "../../config";
import { canManageWorkspaceActions } from "../../workspaceRolePermissions";
import { ConvexAuthGate } from "../auth";
import { UnauthenticatedLandingPage } from "../landing/UnauthenticatedLandingPage";
import { clientDisplayNameFromSlug } from "../shared/clientDisplayName";

function useShellCopy() {
  const location = useLocation();
  const clientMonthMatch = location.pathname.match(/^\/client\/([^/]+)\/([^/]+)/);
  const clientMatch = location.pathname.match(/^\/client\/([^/]+)\/?$/);
  if (location.pathname === "/") {
    return {
      eyebrow: "Agency dashboard",
      title: "Client portfolio",
    };
  }
  if (clientMatch) {
    return {
      eyebrow: "Client Home",
      title: clientDisplayNameFromSlug(clientMatch[1]),
    };
  }
  if (location.pathname === "/company") {
    return {
      eyebrow: "Client Home",
      title: "Client Home",
    };
  }
  if (location.pathname === "/clients") {
    return {
      eyebrow: "Client portfolio",
      title: "Clients",
    };
  }
  if (location.pathname === "/admin" || location.pathname === "/admin/calendars" || location.pathname === "/admin/clients") {
    return {
      eyebrow: "Operations",
      title: location.pathname === "/admin/calendars" ? "Global Calendars" : location.pathname === "/admin/clients" ? "Client Administration" : "Admin",
    };
  }
  if (location.pathname.endsWith("/defaults")) {
    return { eyebrow: "Client configuration", title: "Client Defaults" };
  }
  if (location.pathname.endsWith("/proposal-configuration")) {
    return { eyebrow: "Proposal configuration", title: "Monthly Proposal Configuration" };
  }
  if (location.pathname.startsWith("/client-review/")) {
    return {
      eyebrow: "Client Review",
      title: "Shared review link",
    };
  }
  if (clientMonthMatch) {
    return {
      eyebrow: `${clientDisplayNameFromSlug(clientMonthMatch[1])} · ${clientMonthMatch[2]}`,
      title: "Monthly Proposal",
    };
  }
  return {
    eyebrow: "Content Ops",
    title: "Workspace",
  };
}


export function getShellRouteContext(pathname: string): { clientSlug?: string; month?: string } {
  const clientMonthMatch = pathname.match(/^\/client\/([^/]+)\/([^/]+)/);
  if (clientMonthMatch) {
    return {
      clientSlug: clientMonthMatch[1],
      month: clientMonthMatch[2],
    };
  }

  const clientMatch = pathname.match(/^\/client\/([^/]+)\/?$/);
  if (clientMatch) {
    return {
      clientSlug: clientMatch[1],
    };
  }

  return {};
}

export function getShellNavState(pathname: string): {
  clientHomeParams?: { clientSlug: string };
  stageParams?: { clientSlug: string; month: string };
} {
  const routeContext = getShellRouteContext(pathname);
  return {
    clientHomeParams: routeContext.clientSlug ? { clientSlug: routeContext.clientSlug } : undefined,
    stageParams: routeContext.clientSlug && routeContext.month
      ? { clientSlug: routeContext.clientSlug, month: routeContext.month }
      : undefined,
  };
}

export function Shell() {
  const location = useLocation();
  const clerkEnabled = isConfigured(serviceConfig.clerk.publishableKey);
  const convexEnabled = isConfigured(serviceConfig.convex.url);

  if (location.pathname.startsWith("/client-review/")) {
    return (
      <ContentWorkflowProvider>
        <main className="review-shell">
          <Outlet />
        </main>
      </ContentWorkflowProvider>
    );
  }

  if (clerkEnabled) {
    return (
      <>
        <SignedOut>
          <UnauthenticatedLandingPage />
        </SignedOut>
        <SignedIn>
          <WorkspaceShell clerkEnabled={clerkEnabled} convexEnabled={convexEnabled} />
        </SignedIn>
      </>
    );
  }

  return <WorkspaceShell clerkEnabled={clerkEnabled} convexEnabled={convexEnabled} />;
}

function WorkspaceShell(props: { clerkEnabled: boolean; convexEnabled: boolean }) {
  return props.convexEnabled ? <ConfiguredWorkspaceShell {...props} /> : <WorkspaceShellView {...props} />;
}

function ConfiguredWorkspaceShell(props: { clerkEnabled: boolean; convexEnabled: boolean }) {
  const location = useLocation();
  const routeContext = getShellRouteContext(location.pathname);
  const currentUser = useQuery(api.identity.getCurrentUserMemberships, {});
  const proposalDataset = useQuery(
    api.monthlyProposals.listCompanyProposals,
    routeContext.clientSlug ? { clientSlug: routeContext.clientSlug } : "skip",
  );
  const proposalStatus = routeContext.month
    ? proposalDataset?.proposals.find((proposal) => proposal.month === routeContext.month)?.status
    : undefined;
  const stageSummary = useQuery(
    api.workflowPolicy.getForClientMonth,
    routeContext.clientSlug && routeContext.month
      ? { clientSlug: routeContext.clientSlug, month: routeContext.month }
      : "skip",
  );
  return <WorkspaceShellView {...props} currentUser={currentUser} proposalStatus={proposalStatus} stageSummary={stageSummary} />;
}

function WorkspaceShellView({
  clerkEnabled,
  convexEnabled,
  currentUser,
  proposalStatus,
  stageSummary,
}: {
  clerkEnabled: boolean;
  convexEnabled: boolean;
  currentUser?: { memberships?: any[] };
  proposalStatus?: string;
  stageSummary?: any;
}) {
  const location = useLocation();
  const shellCopy = useShellCopy();
  const shellNavState = getShellNavState(location.pathname);
  const showSetupDiagnostics = !import.meta.env.PROD || !clerkEnabled || !convexEnabled;
  const canViewAdminNav = !convexEnabled || canManageWorkspaceActions({ memberships: currentUser?.memberships });

  return (
    <ContentWorkflowProvider>
      <div className="app-shell">
        <main>
          <header className="global-header">
            <Link to="/" className="global-brand" aria-label="ODC Dashboard">
              <span className="brand-mark">ODC</span>
              <span>Content Ops</span>
            </Link>
            <nav className="global-nav" aria-label="Global navigation">
              <NavLink to="/" icon={<LayoutDashboard size={17} />} label="Dashboard" />
              <NavLink to="/clients" icon={<Users size={17} />} label="Clients" />
              {canViewAdminNav ? <NavLink to="/admin" icon={<Archive size={17} />} label="Admin" /> : null}
            </nav>
            <div className="global-context">
              <p className="eyebrow">{shellCopy.eyebrow}</p>
              <h1>{shellCopy.title}</h1>
            </div>
            <div className="global-actions">
              <AccountAffordance clerkEnabled={clerkEnabled} convexEnabled={convexEnabled} />
            </div>
          </header>
          {shellNavState.stageParams ? <ProposalStageNav params={shellNavState.stageParams} pathname={location.pathname} proposalStatus={proposalStatus} stageSummary={stageSummary} /> : null}
          {showSetupDiagnostics && !clerkEnabled ? <SetupDiagnosticsCard clerkEnabled={clerkEnabled} convexEnabled={convexEnabled} /> : null}
          {convexEnabled && clerkEnabled ? (
            <ConvexAuthGate title="Workspace Access">
              <Outlet />
            </ConvexAuthGate>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </ContentWorkflowProvider>
  );
}

export function getProposalStageReached(proposalStatus?: string) {
  return proposalStatus === "Approved" || proposalStatus === "In client review" || proposalStatus === "client_approved" || proposalStatus === "closed" || proposalStatus === "client_review"
    ? 5
    : proposalStatus === "Design" || proposalStatus === "assets_ready"
      ? 4
      : proposalStatus === "Briefing" || proposalStatus === "briefs_ready"
        ? 3
        : 2;
}

function ProposalStageNav({ params, pathname, proposalStatus, stageSummary }: { params: { clientSlug: string; month: string }; pathname: string; proposalStatus?: string; stageSummary?: any }) {
  const statusReached = getProposalStageReached(proposalStatus);
  const stages = [
    { slug: "proposal-configuration", label: "Month Configuration", policyKey: "configuration" },
    { slug: "calendar", label: "Calendar", policyKey: "calendar" },
    { slug: "briefing", label: "Briefing", policyKey: "briefing" },
    { slug: "design", label: "Design", policyKey: "design" },
    { slug: "client-review", label: "Client Review", policyKey: "clientReview" },
  ];
  const activeStage = stages.findIndex((stage) => pathname.endsWith(`/${stage.slug}`)) + 1;
  const reached = Math.max(statusReached, activeStage);
  return (
    <nav className="proposal-stage-nav" aria-label="Monthly proposal stages">
      {stages.map((stage, index) => {
        const number = index + 1;
        const active = pathname.endsWith(`/${stage.slug}`);
        const policy = stageSummary?.stages?.[stage.policyKey];
        const eligible = policy ? Boolean(policy.accessible) : number <= reached;
        const statusLabel = active
          ? eligible ? "Current" : "Blocked"
          : eligible ? number < reached ? "Completed" : "Available" : "Not yet eligible";
        const contents = <><span className="stage-index">{number}</span><span>{stage.label}</span><small title={policy?.reason}>{statusLabel}</small></>;
        return eligible ? (
          <Link key={stage.slug} to={`/client/$clientSlug/$month/${stage.slug}` as never} params={params as never} className={active ? "active" : undefined}>{contents}</Link>
        ) : (
          <span key={stage.slug} className="proposal-stage-disabled" aria-disabled="true">{contents}</span>
        );
      })}
    </nav>
  );
}

function SetupDiagnosticsCard(props: { clerkEnabled: boolean; convexEnabled: boolean }) {
  const ready = props.clerkEnabled && props.convexEnabled;
  return (
    <div className="service-card">
      <span className={ready ? "status-dot online" : "status-dot"} />
      <div>
        <strong>{ready ? "Workspace services ready" : "Local setup mode"}</strong>
        <span>{ready ? "Sign in to continue" : "Using local demo data"}</span>
      </div>
    </div>
  );
}

function AccountAffordance(props: { clerkEnabled: boolean; convexEnabled: boolean }) {
  if (!props.clerkEnabled) {
    return (
      <div className="account-chip demo-account" aria-label="Demo account">
        <span className="account-avatar">D</span>
        <span className="account-copy">
          <strong>Demo mode</strong>
          <span>Local workspace state</span>
        </span>
      </div>
    );
  }

  return (
    <div className="account-area">
      <SignedOut>
        <SignInButton mode="modal">
          <button className="account-signin" type="button">
            <LogIn size={16} />
            Sign in
          </button>
        </SignInButton>
      </SignedOut>
      <SignedIn>
        <SignedInAccount convexEnabled={props.convexEnabled} />
      </SignedIn>
    </div>
  );
}

function SignedInAccount(props: { convexEnabled: boolean }) {
  const { user } = useUser();
  const displayName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? "Signed in";

  return (
    <div className="account-chip">
      <UserButton />
      <span className="account-copy">
        <strong>{displayName}</strong>
        {props.convexEnabled ? <MembershipSummary /> : <span>Signed in, demo data</span>}
      </span>
    </div>
  );
}

function MembershipSummary() {
  const upsertCurrentUser = useMutation(api.identity.upsertCurrentUser);
  const currentUser = useQuery(api.identity.getCurrentUserMemberships, {});
  const [syncState, setSyncState] = useState<"syncing" | "ready" | "blocked">("syncing");

  useEffect(() => {
    let cancelled = false;
    setSyncState("syncing");
    upsertCurrentUser({})
      .then(() => {
        if (!cancelled) {
          setSyncState("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSyncState("blocked");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [upsertCurrentUser]);

  const membership = currentUser?.memberships?.[0];
  if (membership) {
    const scope = membership.clientAccess.kind === "all" ? membership.organization?.name ?? "All clients" : `${membership.companies.length} clients`;
    return <span>{membership.roles.map(formatRoleLabel).join(" + ")} · {scope}</span>;
  }

  if (syncState === "blocked") {
    return <span>Workspace role pending</span>;
  }

  return <span>{syncState === "ready" ? "No workspace role assigned" : "Checking workspace role"}</span>;
}

function formatRoleLabel(roleName: string) {
  return roleName
    .split("_")
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function NavLink(props: { to: string; icon: JSX.Element; label: string }) {
  return (
    <Link to={props.to as never} aria-label={props.label} activeProps={{ className: "active" }}>
      <span className="global-nav-icon" aria-hidden="true">{props.icon}</span>
      <span className="global-nav-label">{props.label}</span>
    </Link>
  );
}
